import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, ArrowLeft, ArrowUp, ArrowDown, Download, FileText, Inbox, Copy } from "lucide-react";
import { PcoFormSync } from "@/components/PcoFormSync";
import { RichTextEditor } from "@/components/RichTextEditor";
import { toast } from "sonner";

const FIELD_TYPES = [
  { v: "short_text", l: "Short text" }, { v: "long_text", l: "Paragraph" }, { v: "email", l: "Email" },
  { v: "phone", l: "Phone" }, { v: "number", l: "Number" }, { v: "dropdown", l: "Dropdown" },
  { v: "checkbox", l: "Checkboxes (multi-select)" }, { v: "single_checkbox", l: "Single checkbox" }, { v: "date", l: "Date" },
];
const HAS_OPTIONS = ["dropdown", "checkbox"];
const newField = () => ({ id: `fld_${Math.random().toString(36).slice(2, 10)}`, type: "short_text", label: "New field", description: "", placeholder: "", required: false, options: [] });
const blankForm = () => ({ name: "Untitled form", title: "", description: "", submit_text: "Submit", success_message: "Thank you! We've received your response.", fields: [newField()], notify_emails: [], send_confirmation: false, confirmation_subject: "Thank you!", confirmation_message: "", confirmation_message_html: "", confirmation_cta_label: "", confirmation_cta_url: "", whats_next_message: "", email_footer: "", sender_profile_id: null });

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
function EmailChips({ value, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const add = () => {
    const e = input.trim().replace(/,$/, "");
    if (!e) return;
    if (!EMAIL_RE.test(e)) { toast.error("Enter a valid email address"); return; }
    if (!(value || []).includes(e)) onChange([...(value || []), e]);
    setInput("");
  };
  return (
    <div className="mt-1" data-testid="forms-notify-chips">
      <div className="flex flex-wrap gap-2 mb-2">
        {(value || []).map((e) => (
          <span key={e} className="inline-flex items-center gap-1 bg-[#F0EBE1] text-[#2A2626] text-sm px-2.5 py-1 rounded-full" data-testid={`notify-chip-${e}`}>
            {e}
            <button type="button" onClick={() => onChange(value.filter((x) => x !== e))} className="text-[#8A857F] hover:text-[#2A2626]" aria-label={`Remove ${e}`}>×</button>
          </span>
        ))}
      </div>
      <Input
        value={input}
        onChange={(ev) => setInput(ev.target.value)}
        onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === ",") { ev.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={placeholder}
        className="h-10"
        data-testid="forms-notify-input"
      />
      <p className="text-[11px] text-[#8A857F] mt-1">Press Enter to add each recipient.</p>
    </div>
  );
}

export function FormsManager({ onFormsChanged }) {
  const [view, setView] = useState("list"); // list | editor | submissions
  const [forms, setForms] = useState([]);
  const [draft, setDraft] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [subData, setSubData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [senders, setSenders] = useState([]);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/forms"); setForms(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/tenant/senders").then(({ data }) => setSenders(data || [])).catch(() => {}); }, []);

  const startNew = () => { setDraft(blankForm()); setEditingId(null); setView("editor"); };
  const startEdit = async (id) => {
    try { const { data } = await api.get(`/forms/${id}`); setDraft(data); setEditingId(id); setView("editor"); }
    catch (e) { toast.error("Could not load form"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this Connect Card? Existing responses stay stored.")) return;
    try { await api.delete(`/forms/${id}`); toast.success("Connect Card deleted"); load(); onFormsChanged && onFormsChanged(); }
    catch (e) { toast.error("Delete failed"); }
  };
  const duplicateForm = async (id) => {
    try {
      const { data } = await api.get(`/forms/${id}`);
      const copy = {
        ...blankForm(),
        ...data,
        name: `${data.name || "Untitled form"} (copy)`,
        fields: (data.fields || []).map((f) => ({ ...f, options: [...(f.options || [])], id: `fld_${Math.random().toString(36).slice(2, 10)}` })),
      };
      delete copy.form_id; delete copy.tenant_id; delete copy.submission_count; delete copy.created_at; delete copy.updated_at;
      setDraft(copy); setEditingId(null); setView("editor");
      toast.success("Duplicated — review and save this copy");
    } catch (e) { toast.error("Could not duplicate form"); }
  };

  const saveDraft = async () => {
    setLoading(true);
    try {
      const payload = { ...draft, fields: (draft.fields || []).map((f) => ({ ...f, options: HAS_OPTIONS.includes(f.type) ? (f.options || []) : [] })) };
      if (editingId) await api.put(`/forms/${editingId}`, payload);
      else await api.post("/forms", payload);
      toast.success("Connect Card saved");
      setView("list"); setDraft(null); setEditingId(null);
      load(); onFormsChanged && onFormsChanged();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const openSubmissions = async (form) => {
    try { const { data } = await api.get(`/forms/${form.form_id}/submissions`); setSubData(data); setView("submissions"); }
    catch (e) { toast.error("Could not load submissions"); }
  };

  const exportCsv = async (form) => {
    try {
      const res = await api.get(`/forms/${form.form_id}/submissions.csv`, { responseType: "blob" });
      // Prefer the server-provided filename (single source of truth); fall back if absent.
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      const filename = m ? m[1] : `ministryhub-connect-card-${(form.name || "card").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}-submissions.csv`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  // ---------- Field editing helpers ----------
  const updField = (i, patch) => setDraft((d) => ({ ...d, fields: d.fields.map((f, idx) => idx === i ? { ...f, ...patch } : f) }));
  const addField = () => setDraft((d) => ({ ...d, fields: [...d.fields, newField()] }));
  const dupField = (i) => setDraft((d) => {
    const src = d.fields[i];
    const copy = { ...src, options: [...(src.options || [])], id: `fld_${Math.random().toString(36).slice(2, 10)}` };
    const arr = [...d.fields];
    arr.splice(i + 1, 0, copy);
    return { ...d, fields: arr };
  });
  const delField = (i) => setDraft((d) => ({ ...d, fields: d.fields.filter((_, idx) => idx !== i) }));
  const moveField = (i, dir) => setDraft((d) => {
    const arr = [...d.fields]; const j = i + dir;
    if (j < 0 || j >= arr.length) return d;
    [arr[i], arr[j]] = [arr[j], arr[i]]; return { ...d, fields: arr };
  });

  // ---------- LIST ----------
  if (view === "list") {
    return (
      <div className="space-y-4" data-testid="forms-list">
        <div className="flex items-center justify-between">
          <p className="text-sm text-[#5C5856]">Build a Connect Card and attach it to any button.</p>
          <Button onClick={startNew} className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white" data-testid="forms-new"><Plus className="w-4 h-4 mr-1" /> New Connect Card</Button>
        </div>
        {forms.length === 0 && (
          <div className="p-10 text-center rounded-2xl border border-dashed border-[#E5E2DC] text-[#5C5856]">
            <FileText className="w-8 h-8 mx-auto mb-3 text-[#2A2626]" />
            No Connect Cards yet. Create one (e.g. an "I'm New Here" card) and attach it to a button.
          </div>
        )}
        {forms.map((f) => (
          <Card key={f.form_id} className="p-5 flex items-center justify-between" data-testid={`forms-item-${f.form_id}`}>
            <div>
              <h3 className="font-semibold">{f.name}</h3>
              <p className="text-sm text-[#5C5856]">{(f.fields || []).length} fields · {f.submission_count || 0} responses</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => openSubmissions(f)} data-testid={`forms-submissions-${f.form_id}`}><Inbox className="w-4 h-4 mr-1" /> Responses</Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => startEdit(f.form_id)} data-testid={`forms-edit-${f.form_id}`}>Edit</Button>
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => duplicateForm(f.form_id)} data-testid={`forms-duplicate-${f.form_id}`}><Copy className="w-4 h-4 mr-1" /> Duplicate</Button>
              <Button variant="ghost" size="sm" onClick={() => remove(f.form_id)} data-testid={`forms-delete-${f.form_id}`}><Trash2 className="w-4 h-4" /></Button>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  // ---------- SUBMISSIONS ----------
  if (view === "submissions" && subData) {
    const fields = subData.form.fields || [];
    const subs = subData.submissions || [];
    return (
      <div className="space-y-4" data-testid="forms-submissions-view">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => setView("list")} data-testid="forms-sub-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => exportCsv(subData.form)} data-testid="forms-export-csv" disabled={!subs.length}><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
        </div>
        <h3 className="text-xl font-semibold">{subData.form.name} — {subs.length} responses</h3>
        {subs.length === 0 ? (
          <p className="text-[#5C5856] p-8 text-center border border-dashed border-[#E5E2DC] rounded-2xl">No responses yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-[#E5E2DC]">
            <table className="w-full text-sm">
              <thead className="bg-[#F0EBE1]">
                <tr>
                  <th className="text-left p-3 font-medium">Submitted</th>
                  {fields.map((f) => <th key={f.id} className="text-left p-3 font-medium whitespace-nowrap">{f.label}</th>)}
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.submission_id} className="border-t border-[#E5E2DC]">
                    <td className="p-3 text-[#5C5856] whitespace-nowrap">{new Date(s.created_at).toLocaleString()}</td>
                    {fields.map((f) => {
                      let v = s.data?.[f.id];
                      if (Array.isArray(v)) v = v.join(", ");
                      if (typeof v === "boolean") v = v ? "Yes" : "No";
                      return <td key={f.id} className="p-3">{v ?? ""}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  // ---------- EDITOR ----------
  if (view === "editor" && draft) {
    return (
      <div className="space-y-5" data-testid="forms-editor">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => { setView("list"); setDraft(null); }} data-testid="forms-editor-back"><ArrowLeft className="w-4 h-4 mr-1" /> Back</Button>
          <Button onClick={saveDraft} disabled={loading} className="rounded-full bg-[#2A2626] hover:bg-[#1A1717] text-white" data-testid="forms-save">{loading ? "Saving…" : "Save Connect Card"}</Button>
        </div>

        <Card className="p-5 space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div><Label>Card name (internal)</Label><Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="h-10 mt-1" data-testid="forms-name" /></div>
            <div><Label>Submit button text</Label><Input value={draft.submit_text} onChange={(e) => setDraft({ ...draft, submit_text: e.target.value })} className="h-10 mt-1" data-testid="forms-submit-text" /></div>
          </div>
          <div><Label>Header / title (shown on kiosk)</Label><Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Welcome! Tell us about you" className="h-10 mt-1" data-testid="forms-title" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="mt-1" data-testid="forms-description" /></div>
          <div><Label>Success message (after submit)</Label><Input value={draft.success_message} onChange={(e) => setDraft({ ...draft, success_message: e.target.value })} className="h-10 mt-1" data-testid="forms-success" /></div>
        </Card>

        <div className="space-y-3">
          {draft.fields.map((f, i) => (
            <Card key={f.id} className="p-4 space-y-3" data-testid={`forms-field-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#5C5856]">Field {i + 1}</span>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveField(i, -1)}><ArrowUp className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => moveField(i, 1)}><ArrowDown className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => dupField(i)} data-testid={`forms-field-duplicate-${i}`} title="Duplicate field"><Copy className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => delField(i)} data-testid={`forms-field-delete-${i}`}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Type</Label>
                  <Select value={f.type} onValueChange={(v) => updField(i, { type: v })}>
                    <SelectTrigger className="h-10 mt-1" data-testid={`forms-field-type-${i}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Label</Label><Input value={f.label} onChange={(e) => updField(i, { label: e.target.value })} className="h-10 mt-1" data-testid={`forms-field-label-${i}`} /></div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Help text / description</Label><Input value={f.description} onChange={(e) => updField(i, { description: e.target.value })} className="h-9 mt-1" /></div>
                <div><Label className="text-xs">Placeholder</Label><Input value={f.placeholder} onChange={(e) => updField(i, { placeholder: e.target.value })} className="h-9 mt-1" /></div>
              </div>
              {HAS_OPTIONS.includes(f.type) && (
                <div>
                  <Label className="text-xs">Options (one per line)</Label>
                  <Textarea rows={3} value={(f.options || []).join("\n")} onChange={(e) => updField(i, { options: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })} className="mt-1" placeholder={"Option A\nOption B"} data-testid={`forms-field-options-${i}`} />
                </div>
              )}
              <div className="flex items-center gap-3">
                <Switch checked={!!f.required} onCheckedChange={(v) => updField(i, { required: v })} data-testid={`forms-field-required-${i}`} />
                <Label className="text-sm">Required</Label>
              </div>
            </Card>
          ))}
          <Button onClick={addField} variant="outline" className="w-full h-11 rounded-xl border-dashed" data-testid="forms-add-field"><Plus className="w-4 h-4 mr-2" /> Add field</Button>
        </div>

        <Card className="p-5 space-y-4">
          <div>
            <h3 className="font-semibold">Staff notifications</h3>
            <p className="text-xs text-[#5C5856]">Who on your team gets notified when someone submits this card. Add as many as you like. Leave empty to use your church default.</p>
          </div>
          <div>
            <Label className="text-xs">Send response notifications to</Label>
            <EmailChips
              value={draft.notify_emails || []}
              onChange={(list) => setDraft({ ...draft, notify_emails: list })}
              placeholder="office@yourchurch.org"
            />
          </div>

          <div className="pt-4 border-t border-[#E5E2DC]">
            <h3 className="font-semibold">Guest confirmation</h3>
            <p className="text-xs text-[#5C5856]">A thank-you email sent to the person who submitted (uses their Email field).</p>
          </div>
          <div>
            <Label className="text-xs">Send confirmation from</Label>
            <Select value={draft.sender_profile_id || "__default__"} onValueChange={(v) => setDraft({ ...draft, sender_profile_id: v === "__default__" ? null : v })}>
              <SelectTrigger className="h-10 mt-1" data-testid="forms-sender-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__default__">Church default{senders.find((s) => s.is_default) ? ` — ${senders.find((s) => s.is_default).internal_name}` : ""}</SelectItem>
                {senders.map((s) => <SelectItem key={s.profile_id} value={s.profile_id}>{s.internal_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#8A857F] mt-1">Manage senders under Connections → Email.</p>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={!!draft.send_confirmation} onCheckedChange={(v) => setDraft({ ...draft, send_confirmation: v })} data-testid="forms-send-confirmation" />
            <Label className="text-sm">Send a confirmation email to the submitter</Label>
          </div>
          {draft.send_confirmation && (
            <div className="space-y-3">
              <div><Label className="text-xs">Confirmation subject</Label><Input value={draft.confirmation_subject || ""} onChange={(e) => setDraft({ ...draft, confirmation_subject: e.target.value })} className="h-10 mt-1" data-testid="forms-confirmation-subject" /></div>
              <div>
                <Label className="text-xs">Confirmation message</Label>
                <div className="mt-1">
                  <RichTextEditor
                    value={draft.confirmation_message_html || (draft.confirmation_message ? `<p>${draft.confirmation_message}</p>` : "")}
                    onChange={(html) => setDraft({ ...draft, confirmation_message_html: html })}
                    placeholder="Thank you so much for your interest! Here's what to do next…"
                  />
                </div>
                <p className="text-[11px] text-[#8A857F] mt-1">Format text, add bullet lists, and turn words like "Register here" into clickable links.</p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div><Label className="text-xs">Button label (optional)</Label><Input value={draft.confirmation_cta_label || ""} onChange={(e) => setDraft({ ...draft, confirmation_cta_label: e.target.value })} placeholder="Register here" className="h-10 mt-1" data-testid="forms-cta-label" /></div>
                <div><Label className="text-xs">Button link URL (optional)</Label><Input value={draft.confirmation_cta_url || ""} onChange={(e) => setDraft({ ...draft, confirmation_cta_url: e.target.value })} placeholder="https://yourchurch.org/register" className="h-10 mt-1" data-testid="forms-cta-url" /></div>
              </div>
              <div><Label className="text-xs">"What happens next" message (optional)</Label><Textarea value={draft.whats_next_message || ""} onChange={(e) => setDraft({ ...draft, whats_next_message: e.target.value })} placeholder="e.g. A member of our team will reach out within a few days to welcome you." className="mt-1 min-h-[70px]" data-testid="forms-whats-next" /></div>
              <div><Label className="text-xs">Footer / signature (optional)</Label><Input value={draft.email_footer || ""} onChange={(e) => setDraft({ ...draft, email_footer: e.target.value })} placeholder="e.g. Your Church · 123 Main St · (555) 123-4567" className="h-10 mt-1" data-testid="forms-email-footer" /></div>
            </div>
          )}
        </Card>
        {editingId && <PcoFormSync formId={editingId} />}
      </div>
    );
  }

  return null;
}
